import * as THREE from 'three'

export function createFactoryFacility({ palette }) {
  const root = new THREE.Group()
  const geometries = new Set(), materials = new Set()
  const cycleSeconds = 5.5
  const phaseOffset = 0, staticTime = cycleSeconds * .22, arrivingEnd = .10, departureStart = .73, departureEnd = .83
  const outsideX = 10
  const doorCenterX = -.63, doorPostWidth = .18, doorPostSpacing = .96
  const doorWidth = doorPostSpacing - doorPostWidth, oldStopX = -.45, stopX = oldStopX + doorWidth
  const groundY = 0, landingZ = 1.40, insideZ = .45, rearLocalX = -.775
  const rampStartZ = 1.20, rampEndZ = .945, floorY = .10
  const loadingDock = { doorWidth, oldStopX, stopX, rearX: stopX + rearLocalX, groundY, landingX: doorCenterX, landingZ, insideZ }
  const doorway = { x: doorCenterX, width: doorWidth, thresholdZ: .66, landingZ, insideZ }
  const framingBounds = new THREE.Box3(new THREE.Vector3(-3.75, -.02, -1.60), new THREE.Vector3(3.75, 2.50, 2.50))
  function surface(color, dynamic = false) {
    const material = new THREE.MeshBasicMaterial({ color, transparent: dynamic, polygonOffset: true, polygonOffsetFactor: 1, polygonOffsetUnits: 1 })
    materials.add(material)
    return material
  }
  const face = Object.fromEntries(Object.entries(palette).map(([key, color]) => [key, surface(color)]))
  face.gray = surface('#536676')
  const edge = new THREE.LineBasicMaterial({ color: palette.line })
  materials.add(edge)
  const geometry = value => { geometries.add(value); return value }
  const normalFaces = [face.white, face.pale, face.white, face.pale, face.pale, face.white]
  function shape(g, fill, parent = root, dynamic = null) {
    const group = new THREE.Group()
    let fills = fill, lines = edge
    if (dynamic) {
      const clone = original => { const copy = original.clone(); copy.transparent = true; materials.add(copy); dynamic.push(copy); return copy }
      fills = Array.isArray(fill) ? fill.map(clone) : clone(fill)
      lines = clone(edge)
    }
    group.add(new THREE.Mesh(geometry(g), fills))
    group.add(new THREE.LineSegments(geometry(new THREE.EdgesGeometry(g, 25)), lines))
    parent.add(group)
    return group
  }
  function box(w, h, d, x, y, z, fill = normalFaces, parent = root, dynamic = null) {
    const group = shape(new THREE.BoxGeometry(w, h, d), fill, parent, dynamic)
    group.position.set(x, y, z)
    return group
  }
  const ground = new THREE.Mesh(geometry(new THREE.PlaneGeometry(6.8, 4)), face.ground)
  ground.rotation.x = -Math.PI / 2
  ground.position.set(0, -.012, .45)
  root.add(ground)
  box(3.75, .10, 2.25, 0, .05, -.18)
  box(3.40, 1.30, 1.82, 0, .75, -.25)
  box(3.65, .08, 2.07, 0, 1.44, -.25)
  // A smaller upper floor leaves a visible terrace around its roofline.
  box(2.80, .84, 1.50, -.15, 1.90, -.32)
  box(3.02, .08, 1.72, -.15, 2.36, -.32)
  box(1.00, .025, .55, .30, 2.4175, -.46, face.blue)
  // Sparse horizontal glazing leaves the factory's layered massing legible.
  box(2.68, .20, .015, .13, 1.12, .668, face.side)
  box(.015, .20, 1.30, 1.707, 1.12, -.28, face.pale)
  box(2.21, .16, .015, -.10, 2.08, .438, face.side)
  box(.015, .16, 1.05, 1.258, 2.08, -.32, face.pale)
  // One continuous portal avoids the post/lintel outlines cutting through each other.
  const entryShape = new THREE.Shape()
  entryShape.moveTo(-.57, .05)
  entryShape.lineTo(-.57, .83)
  entryShape.lineTo(.57, .83)
  entryShape.lineTo(.57, .05)
  entryShape.lineTo(.39, .05)
  entryShape.lineTo(.39, .69)
  entryShape.lineTo(-.39, .69)
  entryShape.lineTo(-.39, .05)
  entryShape.closePath()
  const entry = shape(new THREE.ExtrudeGeometry(entryShape, { depth: .58, bevelEnabled: false, steps: 1 }), [face.white, face.pale])
  entry.name = 'factory-entry-frame'
  entry.position.set(doorCenterX, 0, .53)
  box(1.35, .075, .78, -.63, .87, .86)
  box(doorWidth, .60, .022, doorCenterX, .40, .677, face.gray)
  // A flush ramp connects road level to the factory plinth without blocking cargo.
  const ramp = new THREE.BufferGeometry()
  const left = doorCenterX - doorWidth / 2, right = doorCenterX + doorWidth / 2
  ramp.setAttribute('position', new THREE.Float32BufferAttribute([
    left, groundY, rampStartZ, right, groundY, rampStartZ, right, floorY, rampEndZ,
    left, groundY, rampStartZ, right, floorY, rampEndZ, left, floorY, rampEndZ,
  ], 3))
  ramp.computeVertexNormals()
  shape(ramp, face.pale)
  const truck = new THREE.Group()
  const truckMaterials = []
  root.add(truck)
  box(1.55, .13, .63, 0, .32, 0, face.gray, truck, truckMaterials)
  box(.94, .09, .66, -.26, .46, 0, face.pale, truck, truckMaterials)
  box(.94, .13, .04, -.26, .55, -.32, face.white, truck, truckMaterials)
  box(.94, .13, .04, -.26, .55, .32, face.white, truck, truckMaterials)
  box(.48, .55, .60, .50, .64, 0, [face.side, face.pale, face.white, face.pale, face.blue, face.white], truck, truckMaterials)
  box(.016, .22, .45, .749, .75, 0, face.pale, truck, truckMaterials)
  box(.26, .23, .016, .51, .76, .309, face.pale, truck, truckMaterials)
  box(.13, .07, .025, .68, .49, .327, face.white, truck, truckMaterials)
  box(.13, .07, .025, .68, .49, -.327, face.white, truck, truckMaterials)
  const wheels = []
  for (const x of [-.48, .49]) for (const z of [-.35, .35]) {
    const wheel = shape(new THREE.CylinderGeometry(.16, .16, .085, 24), face.gray, truck, truckMaterials)
    wheel.rotation.x = Math.PI / 2
    wheel.position.set(x, .22, z)
    const hub = shape(new THREE.CylinderGeometry(.075, .075, .094, 16), face.white, wheel, truckMaterials)
    wheels.push(wheel)
  }
  const packages = Array.from({ length: 2 }, (_, index) => {
    const owned = []
    const item = box(.29, .29, .32, 0, 0, 0, index ? face.side : normalFaces, root, owned)
    item.name = `outbound-package-${index + 1}`
    return { item, materials: owned, index }
  })
  const bounds = new THREE.Mesh(geometry(new THREE.BoxGeometry(7.5, 2.52, 4.10)), face.ground)
  bounds.position.set(0, 1.24, .45)
  bounds.visible = false
  root.add(bounds)
  const smooth = value => { const t = THREE.MathUtils.clamp(value, 0, 1); return t * t * (3 - 2 * t) }
  function update(seconds) {
    // Playback starts outside the frame; staticTime shows the truck parked at the dock.
    const phase = (((seconds / cycleSeconds + phaseOffset) % 1) + 1) % 1
    let x = stopX, state = 'loading', opacity = 1
    if (phase < arrivingEnd) {
      const progress = phase / arrivingEnd
      x = THREE.MathUtils.lerp(-outsideX, stopX, 1 - (1 - progress) ** 4)
      state = 'arriving'
    } else if (phase >= departureStart && phase < departureEnd) {
      const progress = (phase - departureStart) / (departureEnd - departureStart)
      x = THREE.MathUtils.lerp(stopX, outsideX, progress ** 2)
      state = 'departing'
    } else if (phase >= departureEnd) {
      x = outsideX
      opacity = 0
      state = 'hidden'
    }
    truck.position.set(x, 0, 1.79)
    truck.visible = opacity > .001
    truckMaterials.forEach(material => { material.opacity = opacity })
    wheels.forEach(wheel => { wheel.rotation.y = -(x + outsideX) / .16 })
    const cargo = packages.map(({ item, materials: owned, index }) => {
      const start = .20 + index * .20
      const progress = THREE.MathUtils.clamp((phase - start) / .16, 0, 1)
      const sourceX = x - .45 + (1 - index) * .37
      let px = doorCenterX, py = floorY + .145, pz = insideZ, cargoOpacity = 0
      let stage = 'inside'
      let flightProgress = 0, slideProgress = 0
      if (phase >= start && phase < departureEnd) {
        cargoOpacity = 1
        if (progress < .36) {
          stage = 'emerging'
          slideProgress = progress / .36
          pz = THREE.MathUtils.lerp(insideZ, landingZ, smooth(slideProgress))
          // The parcel's trailing edge stays supported until it clears the ramp.
          py = groundY + .145 + floorY * THREE.MathUtils.clamp((rampStartZ - (pz - .16)) / (rampStartZ - rampEndZ), 0, 1)
          if (pz + .16 <= doorway.thresholdZ) cargoOpacity = 0
        } else if (progress < .43) {
          stage = 'landed'
          py = groundY + .145
          pz = landingZ
          slideProgress = 1
        } else if (progress < .78) {
          stage = 'airborne'
          const flight = smooth((progress - .43) / .35)
          flightProgress = flight
          px = THREE.MathUtils.lerp(doorCenterX, loadingDock.rearX, flight)
          pz = THREE.MathUtils.lerp(landingZ, 1.79, flight)
          py = THREE.MathUtils.lerp(groundY + .145, .65, flight) + .24 * 4 * flight * (1 - flight)
          slideProgress = 1
        } else {
          stage = progress < 1 ? 'tailgate' : 'onboard'
          px = progress < 1 ? THREE.MathUtils.lerp(loadingDock.rearX, sourceX, smooth((progress - .78) / .22)) : sourceX
          py = .65
          pz = 1.79
          flightProgress = 1
          slideProgress = 1
        }
      }
      if (phase >= departureEnd) { cargoOpacity = 0; stage = 'hidden' }
      item.position.set(px, py, pz)
      item.visible = cargoOpacity > .001
      owned.forEach(material => { material.opacity = cargoOpacity })
      return { x: px, y: py, z: pz, bottomY: py - .145, groundY, opacity: cargoOpacity, visible: item.visible, progress, flightProgress, slideProgress, stage }
    })
    Object.assign(root.userData, { phase, cycleSeconds, phaseOffset, arrivingEnd, departureStart, departureEnd, loadingDock, doorway, parkedX: stopX, rearX: loadingDock.rearX, truck: { x, opacity, visible: truck.visible, state }, cargo })
    return root.userData
  }
  update(staticTime)
  return { root, update, cycleSeconds, duration: cycleSeconds, staticTime, framingBounds, phaseOffset, arrivingEnd, departureStart, departureEnd, getState: () => root.userData, dispose() {
    geometries.forEach(value => value.dispose())
    materials.forEach(value => value.dispose())
  } }
}
